export interface MenuItem {
  name: string;
  price: string;
  desc?: string;
}

export interface MenuTab {
  id: string;
  label: string;
  items: MenuItem[];
  note?: string;
}

export interface Feature {
  icon: string;
  title: string;
  description: string;
}

export interface Special {
  icon: string;
  title: string;
  time: string;
  description: string;
}

export const navLinks = [
  { label: 'About', href: '#about' },
  { label: 'Menu', href: '#menu' },
  { label: 'Specials', href: '#specials' },
  { label: 'Reservations', href: '#reservation' },
  { label: 'Contact', href: '#contact' },
];

export const aboutFeatures: Feature[] = [
  {
    icon: 'ri-restaurant-line',
    title: 'Dining in Elegance',
    description: 'A curated menu featuring locally sourced ingredients and innovative culinary techniques.',
  },
  {
    icon: 'ri-goblet-line',
    title: 'Craft Cocktails',
    description: 'Expertly crafted beverages using premium spirits and fresh, seasonal ingredients.',
  },
  {
    icon: 'ri-sparkling-line',
    title: 'Elegant Atmosphere',
    description: 'A modern, sophisticated dining space designed for comfort and celebration.',
  },
];

export const galleryItems = [
  { label: 'Ambiance', size: 'large' },
  { label: 'The Bar', size: 'small' },
  { label: 'Dining Room', size: 'small' },
  { label: 'Events', size: 'small' },
];

export const menuTabs: MenuTab[] = [
  {
    id: 'appetizers',
    label: 'Appetizers',
    items: [
      { name: 'Ackee and Breadfruit (3)', price: '$15' },
      { name: 'Conch Fritters (3)', price: '$15' },
      { name: 'Codfish Fritters (3)', price: '$13' },
      { name: 'Jerk Wings (3)', price: '$13' },
      { name: 'Callaloo and Breadfruit', price: '$10' },
      { name: 'Cod Fish and Breadfruit', price: '$12' },
      { name: 'Breadfruit Trio', price: '$17', desc: 'Ackee, Codfish & Callaloo — no substitutions' },
      { name: "Bertie's Island Slider Burgers (2)", price: '$12' },
      { name: 'Tacos (3)', price: '$16', desc: 'Shrimp, Jerk Chicken, or Cod — Thursdays Only' },
      { name: 'Soup of the Day', price: '$8' },
    ],
  },
  {
    id: 'sides',
    label: 'Sides',
    items: [
      { name: 'White Rice', price: '$5' },
      { name: 'Rice and Peas', price: '$6' },
      { name: 'Coconut Rice', price: '$6' },
      { name: 'Mac & Cheese', price: '$8' },
      { name: 'Plantains (6)', price: '$6' },
      { name: 'Truffle Fries', price: '$7' },
      { name: 'Callaloo', price: '$7' },
      { name: 'Steamed Vegetables', price: '$7' },
      { name: 'Festival (3)', price: '$6' },
      { name: 'Fried Bammy (2)', price: '$6' },
      { name: 'Corn Bread', price: '$6' },
    ],
  },
  {
    id: 'entrees',
    label: 'Entrées',
    note: 'All entrées served with rice of choice and shredded cabbage with carrots.',
    items: [
      { name: 'Rasta Pasta', price: '$26', desc: 'Chicken Included: Shrimp +$4, Salmon +$8' },
      { name: 'Chicken', price: '$26', desc: 'Jerk, Stew, Curry, or Jerk BBQ' },
      { name: 'Oxtail', price: '$38' },
      { name: 'Curry Goat', price: '$32' },
      { name: "Bertie's Island Hopper Platter", price: '$39', desc: 'Jerk Chicken, Curry Goat & Oxtail' },
      { name: 'Snapper Fish', price: '$38', desc: 'Escoviche, Steamed, or Brown Stew' },
      { name: 'Salmon Fillet', price: '$26', desc: 'Pan Seared, Grilled, or Curry' },
      {
        name: "Chef's Specials",
        price: '[Market Price]',
        desc: 'Cow Foot, Stew Beef, Stew Peas, Seafood Fridays, Curry Shrimp, Conch',
      },
      { name: 'Vegetarian Delight', price: '$19', desc: 'Channa Curry, Steamed Vegetables & Rice of Choice' },
      { name: 'Vegetarian Rasta Pasta', price: '$19' },
    ],
  },
  {
    id: 'brunch',
    label: 'Brunch',
    note: 'Saturdays & Sundays — 11:00 AM to 3:00 PM. Served with rice, bammy, festival, or provisions.',
    items: [
      { name: "Bertie's Jamaican Breakfast", price: '$38', desc: 'Choice of 2: Saltfish, Callaloo, Stew Chicken' },
      { name: 'Ackee & Saltfish', price: '$25' },
      { name: 'Chicken and Waffles', price: '$18' },
      { name: 'Jerk Lamb Chops (3)', price: '$35' },
      { name: 'Snapper Fish', price: '$35', desc: 'Escoviche, Steamed, or Brown Stew' },
      { name: 'King Fish (2)', price: '$24', desc: 'Fried or Escoviche' },
      { name: 'Grits', price: '$22', desc: 'Shrimp or Fish' },
    ],
  },
  {
    id: 'desserts',
    label: 'Desserts',
    items: [{ name: 'Daily Specials', price: '', desc: 'Ask your server about today\u2019s selections.' }],
  },
  {
    id: 'drinks',
    label: 'Drinks',
    items: [
      { name: 'Cocktails', price: '$16' },
      { name: 'Well Drinks', price: '$10' },
      { name: 'Azul (per shot)', price: '$36' },
      { name: '1942 (per shot)', price: '$40' },
      { name: 'Top Shelf (per shot)', price: '$22' },
      { name: 'Well (per shot)', price: '$18' },
      { name: "Bertie's Rum Punch", price: '$16' },
      { name: 'Fountain Drinks', price: '$4' },
      { name: 'Island Drinks', price: '$7', desc: 'Sorrel Ginger, Pineapple Ginger, Fruit Punch, or Mango' },
    ],
  },
];

export const specials: Special[] = [
  {
    icon: 'ri-goblet-fill',
    title: 'Happy Hour',
    time: 'Wed – Fri, 4PM – 7:00PM',
    description: 'Join us for signature cocktails and island bites at relaxed evening pricing.',
  },
  {
    icon: 'ri-restaurant-2-fill',
    title: 'Weekend Brunch',
    time: 'Sat & Sun, 11AM – 3PM',
    description: 'An exclusive Caribbean brunch menu served all weekend long.',
  },
];

export const contactInfo = {
  phone: '(786) 424-7388',
  phoneHref: 'tel:7864247388',
  address: '2088 Badlands Drive, Brandon, Florida 33511',
  hours: [
    { day: 'Wed – Fri', time: '3PM – 11PM' },
    { day: 'Saturday', time: '11AM – 11PM' },
    { day: 'Sunday', time: '11AM – 3PM & 4PM – 8PM' },
  ],
};

export const socialLinks = {
  instagram: 'https://www.instagram.com/bertiesrestaurantandcocktails',
  tiktok: 'https://www.tiktok.com/@bertiesrestaurant2088',
  facebook: 'https://www.facebook.com/share/18VzJqQYkB/',
};